import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Trash2, RefreshCw, Filter } from 'lucide-react';
import { Attachments } from '../components/AIFeedbackButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AIMemoryList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('active');
  const [filterCategory, setFilterCategory] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ai-memory?status=${status}`, { withCredentials: true });
      setRows(r.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async (id) => {
    try { await axios.patch(`${API}/ai-memory/${id}`, { status: 'resolved' }, { withCredentials: true }); toast.success('Resolved'); load(); }
    catch { toast.error('Failed'); }
  };
  const reactivate = async (id) => {
    try { await axios.patch(`${API}/ai-memory/${id}`, { status: 'active' }, { withCredentials: true }); toast.success('Reactivated'); load(); }
    catch { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this memory permanently?')) return;
    try { await axios.delete(`${API}/ai-memory/${id}`, { withCredentials: true }); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };
  const purge = async () => {
    if (!window.confirm('Delete all resolved memories older than 90 days?')) return;
    try {
      const r = await axios.post(`${API}/ai-memory/auto-purge`, {}, { withCredentials: true });
      toast.success(`Purged ${r.data.deleted} memor${r.data.deleted === 1 ? 'y' : 'ies'}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const categories = Array.from(new Set(rows.map(r => r.category))).sort();
  const filtered = filterCategory === 'all' ? rows : rows.filter(r => r.category === filterCategory);
  const sevColor = s => s === 'high' ? 'text-red-300 bg-red-900/30 border-red-700/50' : s === 'medium' ? 'text-amber-300 bg-amber-900/30 border-amber-700/50' : 'text-zinc-300 bg-zinc-800 border-zinc-700';

  return (
    <div className="space-y-5" data-testid="ai-memory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Grievance / Issue Notices</h1>
          <p className="text-zinc-500 text-xs mt-0.5">Issues the AI remembers across clients, products, and vendors.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800" data-testid="ai-memory-refresh"><RefreshCw size={13} /></button>
          <button onClick={purge} data-testid="ai-memory-purge"
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-red-300">
            <Trash2 size={12} /> Purge resolved &gt;90d
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {['active', 'resolved', 'all'].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`ai-memory-status-${s}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${status === s ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
            {s.toUpperCase()}
          </button>
        ))}
        {categories.length > 0 && (
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-300 px-3 py-1.5" data-testid="ai-memory-category-filter">
            <option value="all">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>
      {loading && <div className="text-zinc-500 text-sm animate-pulse">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 text-center text-zinc-500 text-sm">
          No {status === 'all' ? '' : status} memories to show.
        </div>
      )}
      {filtered.map(m => (
        <div key={m.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`ai-memory-row-${m.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <AlertTriangle size={12} className="text-amber-400" />
                <span className="text-zinc-100 text-sm font-semibold truncate">{m.summary}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${sevColor(m.severity)}`}>{m.severity}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 capitalize">{m.category}</span>
                {m.linked_entity_name && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-300 capitalize">{m.linked_entity_type}: {m.linked_entity_name}</span>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {new Date(m.created_at).toLocaleString()} · by {m.created_by || '—'}
                {m.status === 'resolved' && m.resolved_at && ` · resolved ${new Date(m.resolved_at).toLocaleDateString()}`}
              </div>
              {m.suggested_solution && (
                <div className="mt-2 bg-emerald-900/15 border-l-2 border-emerald-500 pl-3 py-1.5 text-[12px] text-emerald-200 italic">
                  💡 {m.suggested_solution}
                </div>
              )}
              <div className="text-[11px] text-zinc-400 mt-2 italic">Original: "{m.text}"</div>
              <div className="mt-2">
                <Attachments
                  memoryId={m.id}
                  attachments={m.attachments || []}
                  onChange={(atts) => setRows(rows.map(r => r.id === m.id ? { ...r, attachments: atts } : r))}
                  compact
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {m.status === 'active' ? (
                <button onClick={() => resolve(m.id)} data-testid={`ai-memory-resolve-${m.id}`}
                  className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-green-900/30 text-green-300 hover:bg-green-900/60 font-semibold">
                  <CheckCircle size={11} /> Resolve
                </button>
              ) : (
                <button onClick={() => reactivate(m.id)} data-testid={`ai-memory-reactivate-${m.id}`}
                  className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-amber-900/60 font-semibold">
                  <RefreshCw size={11} /> Reactivate
                </button>
              )}
              <button onClick={() => del(m.id)} data-testid={`ai-memory-delete-${m.id}`}
                className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-red-900/30 text-red-300 hover:bg-red-900/60 font-semibold">
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
